import { useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import styles from './SearchInput.module.css';

interface SearchInputProps {
  /** The value in force — that is, what is in the URL and what was requested. */
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  /** Uppercases as you type — for a code the server stores upper-cased anyway. */
  upper?: boolean;
  /** Width of the field. A code needs far less room than a free-text box. */
  width?: 'sm' | 'md' | 'lg';
}

/** Long enough to finish a word, short enough that the table keeps up with typing. */
const DEBOUNCE_MS = 350;

/**
 * A labelled filter field that commits after a pause.
 *
 * It commits on a pause rather than per keystroke because the committed value goes
 * into the URL and fires a request: writing per character would mean a query per
 * character.
 *
 * **Uncontrolled, deliberately.** A `useState` draft plus an effect syncing it from
 * the prop sets state during the commit on every external change, which React's own
 * guidance is against. A controlled input keyed off the URL is worse: each commit
 * rewrites the URL, which re-renders and resets the field the reader is still typing
 * in. So the DOM node holds what is being typed, and the effect below nudges it back
 * into line with the URL only while the reader is NOT in this field — which is
 * exactly when Back, a shared link or "Clear all" should be allowed to change it.
 *
 * The board's column filters do the same thing in a table header; this is the
 * toolbar-sized version, with its label attached and room for a word rather than a
 * three-letter code.
 */
export const SearchInput = ({
  value,
  onChange,
  label,
  placeholder,
  upper = false,
  width = 'md',
}: SearchInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    // Never yank the text out from under someone mid-word.
    if (document.activeElement === input) return;
    if (input.value !== value) input.value = value;
  }, [value]);

  // A keystroke still waiting to commit when the reader navigates away must not fire
  // afterwards.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    if (upper) {
      // Uppercase in place, restoring the caret: without this, correcting the middle
      // of a code sends the cursor to the end after every character.
      const caret = input.selectionStart;
      input.value = input.value.toUpperCase();
      if (caret !== null) input.setSelectionRange(caret, caret);
    }

    const next = input.value;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onChange(next), DEBOUNCE_MS);
  };

  const clear = () => {
    // Drop a keystroke that has not committed yet, or it would land after the clear
    // and put the text straight back.
    window.clearTimeout(timer.current);

    const input = inputRef.current;
    if (input) {
      input.value = '';
      // Focus goes to the field, not nowhere: clearing a filter is usually the start
      // of typing a different one.
      input.focus();
    }

    onChange('');
  };

  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>

      <span className={styles.control}>
        <input
          ref={inputRef}
          type="text"
          className={cx(styles.input, styles[width], value !== '' && styles.active)}
          defaultValue={value}
          onChange={handleChange}
          // Required by the clear button, which hides itself with
          // `:placeholder-shown` — a selector that only matches while one exists.
          placeholder={placeholder ?? label}
          spellCheck={false}
          autoComplete="off"
        />

        {/*
          Shown only when the field has text, and by CSS rather than by state: the
          input is uncontrolled, so a boolean for this would have to be kept in step
          with the DOM node from three places. `:placeholder-shown` already knows, and
          it knows a keystroke ahead of the debounce — which is what makes the button
          feel attached to the field rather than to the request.

          `visibility: hidden` also keeps it out of the tab order while it is
          invisible, so a row of empty filters is not twice as many tab stops.
        */}
        <button
          type="button"
          className={styles.clear}
          onClick={clear}
          aria-label={t('schedule.clearFilter', { name: label })}
        >
          ×
        </button>
      </span>
    </label>
  );
};
